import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { loadProject, saveProject } from "../../../database/mockProjectStore";
// useDrawSpaceData.js


// Ensuite, vous pouvez l'utiliser directement dans vos appels comme indiqué dans votre code.












/*
====================================================================
FACTORY FUNCTIONS
====================================================================
*/

const createProjectObject = (projectId) => ({
  id: projectId,
  sites: [],
  corridors: [],
});

const createLevelObject = (index = 1, outlineGeometry = null, context = {}) => ({
  id: `level_${Date.now()}_${index}`,

  // UI
  label: index === 1 ? "Ground Floor" : `Level ${index}`,

  // Corelia minimal mapping
  LevelCode: context.LevelCode ?? `LVL_${Date.now()}_${index}`,
  ProjectCode: context.ProjectCode ?? null,
  SiteCode: context.SiteCode ?? null,
  AssetCode: context.AssetCode ?? null,
  IsActive: true,

  // Metrics
  elevation: context.Elevation ?? (index === 1 ? 0 : (index - 1) * 3),
  height: context.Height ?? 3,
  order: index,

  // géométrie structurelle du niveau
  outlineGeometry,

  // Entities dessinées dans le niveau
  entities: [],
  interfaceObjects: [],
});

const createOuvrageObject = (index = 1, footprintGeometry = null, context = {}) => ({
  id: `ouvrage_${Date.now()}_${index}`,

  // UI
  label: `Ouvrage ${index}`,

  // Corelia minimal mapping
  AssetCode: context.AssetCode ?? `AST_${Date.now()}_${index}`,
  ProjectCode: context.ProjectCode ?? null,
  SiteCode: context.SiteCode ?? null,
  TypologyCode: context.TypologyCode ?? "",
  IsActive: true,

  // Géométrie
  footprintGeometry,

  // Données spatiales
  entities: [],
  interfaceObjects: [],
  levels: [],
});

const createSiteObject = (index = 1, context = {}) => ({
  id: `site_${Date.now()}_${index}`,
  label: `Site ${index}`,

  // entités directement rattachées au site
  entities: [],
  interfaceObjects: [],

  // un site peut être vide au départ
  ouvrages: [],


    // Corelia mapping
  ProjectCode: context.ProjectCode ?? null,
  // Enrichissement Business
  area: 0, // Surface en m², initialisée à 0
  perimeter: 0, // Périmètre en m, initialisé à 0
  typologyCode: '', // Typologie (par exemple : "residential", "commercial", etc.)
  status: 'draft', // Statut par défaut ("draft" ou "active")

  
  
  

});



/*
====================================================================
HOOK PRINCIPAL
====================================================================
*/

export default function useDrawSpaceData({
  projectId,
  assetId,
  enablePersistence = true,
}) {
  /*
  ================================================================
  STATES
  ================================================================
  */

  const [project, setProject] = useState(null);

  const [activeSiteId, setActiveSiteId] = useState(null);
  const [activeOuvrageId, setActiveOuvrageId] = useState(null);
  const [activeLevelId, setActiveLevelId] = useState(null);

  const [loading, setLoading] = useState(false);
  const initializedProjectRef = useRef(null);

  /*
  ================================================================
  DONNÉES DÉRIVÉES
  ================================================================
  */

  const sites = useMemo(() => project?.sites ?? [], [project]);

  /*
  ================================================================
  INITIALISATION
  ================================================================
  */

 useEffect(() => {
  if (!projectId) return;

  setLoading(true);

  loadProject(projectId).then((existing) => {
    if (existing && Array.isArray(existing.sites)) {
      const normalizedProject = {
        ...existing,
        sites: existing.sites.map((site) => ({
          ...site,
          ProjectCode: site.ProjectCode ?? projectId,
        })),
      };

      setProject(normalizedProject);

      const firstSite = normalizedProject.sites[0] ?? null;
      setActiveSiteId(firstSite?.id ?? null);
    }
    else {
          const nextProject = createProjectObject(projectId);
          const initialSite = createSiteObject(1);

          nextProject.sites = [initialSite];

          setProject(nextProject);
          setActiveSiteId(initialSite.id);

          saveProject(projectId, nextProject);
        }

        setLoading(false);
      });

}, [projectId]);

  /*
  ================================================================
  ÉLÉMENTS ACTIFS
  ================================================================
  */

  const activeSite = useMemo(() => {
    return sites.find((site) => site.id === activeSiteId) ?? null;
  }, [sites, activeSiteId]);

  const activeOuvrage = useMemo(() => {
    return (
      activeSite?.ouvrages?.find((ouvrage) => ouvrage.id === activeOuvrageId) ??
      null
    );
  }, [activeSite, activeOuvrageId]);

  const activeLevel = useMemo(() => {
    return (
      activeOuvrage?.levels?.find((level) => level.id === activeLevelId) ?? null
    );
  }, [activeOuvrage, activeLevelId]);

  /*
  ===============================================================
  ENTITÉS DU CONTEXTE ACTIF
  ===============================================================
  */

  const getActiveEntities = useCallback(() => {
    return activeLevel?.entities ?? [];
  }, [activeLevel]);

  const getActiveSiteEntities = useCallback(() => {
    return activeSite?.entities ?? [];
  }, [activeSite]);

  const getActiveOuvrageEntities = useCallback(() => {
    return activeOuvrage?.entities ?? [];
  }, [activeOuvrage]);

  const getEntitiesForActiveContext = useCallback(() => {
    if (activeLevelId && activeLevel) return activeLevel.entities ?? [];
    if (activeOuvrageId && activeOuvrage) return activeOuvrage.entities ?? [];
    if (activeSiteId && activeSite) return activeSite.entities ?? [];
    return [];
  }, [
    activeSiteId,
    activeOuvrageId,
    activeLevelId,
    activeSite,
    activeOuvrage,
    activeLevel,
  ]);

  const updateEntitiesForActiveLevel = useCallback(
    (entities) => {
      if (!activeSiteId || !activeOuvrageId || !activeLevelId) return;

      setProject((prev) => {
        if (!prev) return prev;

        return {
          ...prev,
          sites: prev.sites.map((site) => {
            if (site.id !== activeSiteId) return site;

            return {
              ...site,
              ouvrages: site.ouvrages.map((ouvrage) => {
                if (ouvrage.id !== activeOuvrageId) return ouvrage;

                return {
                  ...ouvrage,
                  levels: ouvrage.levels.map((level) => {
                    if (level.id !== activeLevelId) return level;

                    return {
                      ...level,
                      entities: Array.isArray(entities) ? entities : [],
                    };
                  }),
                };
              }),
            };
          }),
        };
      });
    },
    [activeSiteId, activeOuvrageId, activeLevelId]
  );

  const updateEntitiesForActiveSite = useCallback(
    (entities) => {
      if (!activeSiteId) return;

      setProject((prev) => {
        if (!prev) return prev;

        return {
          ...prev,
          sites: prev.sites.map((site) => {
            if (site.id !== activeSiteId) return site;

            return {
              ...site,
              entities: Array.isArray(entities) ? entities : [],
            };
          }),
        };
      });
    },
    [activeSiteId]
  );

  const updateEntitiesForActiveOuvrage = useCallback(
    (entities) => {
      if (!activeSiteId || !activeOuvrageId) return;

      setProject((prev) => {
        if (!prev) return prev;

        return {
          ...prev,
          sites: prev.sites.map((site) => {
            if (site.id !== activeSiteId) return site;

            return {
              ...site,
              ouvrages: site.ouvrages.map((ouvrage) => {
                if (ouvrage.id !== activeOuvrageId) return ouvrage;

                return {
                  ...ouvrage,
                  entities: Array.isArray(entities) ? entities : [],
                };
              }),
            };
          }),
        };
      });
    },
    [activeSiteId, activeOuvrageId]
  );

  const createEntityOnSite = useCallback(
    (entity) => {
      if (!activeSiteId || !entity) return;

      setProject((prev) => {
        if (!prev) return prev;

        return {
          ...prev,
          sites: prev.sites.map((site) => {
            if (site.id !== activeSiteId) return site;

            return {
              ...site,
              entities: [...(site.entities ?? []), entity],
            };
          }),
        };
      });
    },
    [activeSiteId]
  );

const createInterfaceObjectOnSite = useCallback(
  (interfaceObject) => {
    if (!activeSiteId || !interfaceObject) return;

    setProject((prev) => {
      if (!prev) return prev;

      return {
        ...prev,
        sites: prev.sites.map((site) => {
          if (site.id !== activeSiteId) return site;

          return {
            ...site,
            interfaceObjects: [...(site.interfaceObjects ?? []), interfaceObject],
          };
        }),
      };
    });
  },
  [activeSiteId]
);

  const createEntityOnOuvrage = useCallback(
    (entity) => {
      if (!activeSiteId || !activeOuvrageId || !entity) return;

      setProject((prev) => {
        if (!prev) return prev;

        return {
          ...prev,
          sites: prev.sites.map((site) => {
            if (site.id !== activeSiteId) return site;

            return {
              ...site,
              ouvrages: site.ouvrages.map((ouvrage) => {
                if (ouvrage.id !== activeOuvrageId) return ouvrage;

                return {
                  ...ouvrage,
                  entities: [...(ouvrage.entities ?? []), entity],
                };
              }),
            };
          }),
        };
      });
    },
    [activeSiteId, activeOuvrageId]
  );

  const createInterfaceObjectOnOuvrage = useCallback(
  (interfaceObject) => {
    if (!activeSiteId || !activeOuvrageId || !interfaceObject) return;

    setProject((prev) => {
      if (!prev) return prev;

      return {
        ...prev,
        sites: prev.sites.map((site) => {
          if (site.id !== activeSiteId) return site;

          return {
            ...site,
            ouvrages: site.ouvrages.map((ouvrage) => {
              if (ouvrage.id !== activeOuvrageId) return ouvrage;

              return {
                ...ouvrage,
                interfaceObjects: [...(ouvrage.interfaceObjects ?? []), interfaceObject],
              };
            }),
          };
        }),
      };
    });
  },
  [activeSiteId, activeOuvrageId]
);

  const createEntityOnLevel = useCallback(
    (entity) => {
      if (!activeSiteId || !activeOuvrageId || !activeLevelId || !entity) return;

      setProject((prev) => {
        if (!prev) return prev;

        return {
          ...prev,
          sites: prev.sites.map((site) => {
            if (site.id !== activeSiteId) return site;

            return {
              ...site,
              ouvrages: site.ouvrages.map((ouvrage) => {
                if (ouvrage.id !== activeOuvrageId) return ouvrage;

                return {
                  ...ouvrage,
                  levels: ouvrage.levels.map((level) => {
                    if (level.id !== activeLevelId) return level;

                    return {
                      ...level,
                      entities: [...(level.entities ?? []), entity],
                    };
                  }),
                };
              }),
            };
          }),
        };
      });
    },
    [activeSiteId, activeOuvrageId, activeLevelId]
  );

  const createInterfaceObjectOnLevel = useCallback(
  (interfaceObject) => {
    if (!activeSiteId || !activeOuvrageId || !activeLevelId || !interfaceObject) return;

    setProject((prev) => {
      if (!prev) return prev;

      return {
        ...prev,
        sites: prev.sites.map((site) => {
          if (site.id !== activeSiteId) return site;

          return {
            ...site,
            ouvrages: site.ouvrages.map((ouvrage) => {
              if (ouvrage.id !== activeOuvrageId) return ouvrage;

              return {
                ...ouvrage,
                levels: ouvrage.levels.map((level) => {
                  if (level.id !== activeLevelId) return level;

                  return {
                    ...level,
                    interfaceObjects: [...(level.interfaceObjects ?? []), interfaceObject],
                  };
                }),
              };
            }),
          };
        }),
      };
    });
  },
  [activeSiteId, activeOuvrageId, activeLevelId]
);
  /*
  ===============================================================
  UPDATE STRUCTURE
  ===============================================================
  */

  const updateOuvrageById = useCallback(
    (ouvrageId, patch) => {
      if (!activeSiteId || !ouvrageId || !patch) return;

      setProject((prev) => {
        if (!prev) return prev;

        return {
          ...prev,
          sites: prev.sites.map((site) => {
            if (site.id !== activeSiteId) return site;

            return {
              ...site,
              ouvrages: site.ouvrages.map((ouvrage) =>
                ouvrage.id === ouvrageId
                  ? {
                      ...ouvrage,
                      label:
                        patch.label !== undefined ? patch.label : ouvrage.label,
                      ...patch,
                    }
                  : ouvrage
              ),
            };
          }),
        };
      });
    },
    [activeSiteId]
  );

  const updateLevelById = useCallback(
    (levelId, patch) => {
      if (!activeSiteId || !activeOuvrageId || !levelId || !patch) return;

      setProject((prev) => {
        if (!prev) return prev;

        return {
          ...prev,
          sites: prev.sites.map((site) => {
            if (site.id !== activeSiteId) return site;

            return {
              ...site,
              ouvrages: site.ouvrages.map((ouvrage) => {
                if (ouvrage.id !== activeOuvrageId) return ouvrage;

                return {
                  ...ouvrage,
                                    levels: ouvrage.levels.map((level) =>
                    level.id === levelId
                      ? {
                          ...level,
                          label:
                            patch.label !== undefined ? patch.label : level.label,
                          elevation:
                            patch.elevation !== undefined
                              ? patch.elevation
                              : level.elevation,
                          height:
                            patch.height !== undefined
                              ? patch.height
                              : level.height,
                          ...patch,
                        }
                      : level
                  ),
                };
              }),
            };
          }),
        };
      });
    },
    [activeSiteId, activeOuvrageId]
  );

  /*
===============================================================
GESTION DES SITES
===============================================================
*/

// Fonction pour mettre à jour les attributs de Site
const updateSiteAttributes = useCallback(
  (siteId, attributes) => {
    if (!siteId || !attributes) return;

    setProject((prev) => {
      if (!prev) return prev;

      return {
        ...prev,
        sites: prev.sites.map((site) => {
          if (site.id !== siteId) return site;

          return {
            ...site,
            label:
              attributes.label !== undefined ? attributes.label : site.label,
            area:
              attributes.area !== undefined ? attributes.area : site.area,
            perimeter:
              attributes.perimeter !== undefined
                ? attributes.perimeter
                : site.perimeter,
            typologyCode:
              attributes.typologyCode !== undefined
                ? attributes.typologyCode
                : site.typologyCode,
            status:
              attributes.status !== undefined
                ? attributes.status
                : site.status,
          };
        }),
      };
    });
  },
  [setProject]
);

const createSite = useCallback(() => {
  const nextIndex = sites.length + 1;
  const newSite = createSiteObject(nextIndex, {
    ProjectCode: project?.id ?? null,
  });

  setProject((prev) => {
    if (!prev) return prev;

    return {
      ...prev,
      sites: [...prev.sites, newSite],
    };
  });

  setActiveSiteId(newSite.id);
  setActiveOuvrageId(null);
  setActiveLevelId(null);
}, [sites.length, setProject, setActiveSiteId, setActiveOuvrageId, setActiveLevelId, project]);

const deleteSite = useCallback(
  (siteId) => {
    if (!siteId) return;

    const nextSites = sites.filter((site) => site.id !== siteId);

    setProject((prev) => {
      if (!prev) return prev;

      return {
        ...prev,
        sites: nextSites,
      };
    });

    if (nextSites.length > 0) {
      const nextSite = nextSites[0];
      const nextOuvrage = nextSite.ouvrages?.[0] ?? null;
      const nextLevel = nextOuvrage?.levels?.[0] ?? null;

      setActiveSiteId(nextSite.id);
      setActiveOuvrageId(nextOuvrage?.id ?? null);
      setActiveLevelId(nextLevel?.id ?? null);
    } else {
      setActiveSiteId(null);
      setActiveOuvrageId(null);
      setActiveLevelId(null);
    }
  },
  [sites, setProject, setActiveSiteId, setActiveOuvrageId, setActiveLevelId]
);
 
  /*
  ================================================================
  GESTION DES OUVRAGES
  ================================================================
  */

  const createOuvrage = useCallback(() => {
    if (!activeSiteId) return;

    const nextIndex = (activeSite?.ouvrages?.length ?? 0) + 1;
    const newOuvrage = createOuvrageObject(nextIndex, null, {
      ProjectCode: project?.id ?? null,
      SiteCode: activeSite?.id ?? null,
      TypologyCode: activeSite?.typologyCode ?? "",
    });

    setProject((prev) => {
      if (!prev) return prev;

      return {
        ...prev,
        sites: prev.sites.map((site) => {
          if (site.id !== activeSiteId) return site;

          return {
            ...site,
            ouvrages: [...site.ouvrages, newOuvrage],
          };
        }),
      };
    });

    setActiveOuvrageId(newOuvrage.id);
    setActiveLevelId(null);
    }, [activeSiteId, activeSite, project]);

  const createOuvrageFromGeometry = useCallback(
    (geometry) => {
      if (!activeSiteId || !geometry) return;

      const nextIndex = (activeSite?.ouvrages?.length ?? 0) + 1;
      const newOuvrage = createOuvrageObject(nextIndex, geometry, {
        ProjectCode: project?.id ?? null,
        SiteCode: activeSite?.id ?? null,
        TypologyCode: activeSite?.typologyCode ?? "",
      });

      setProject((prev) => {
        if (!prev) return prev;

        return {
          ...prev,
          sites: prev.sites.map((site) => {
            if (site.id !== activeSiteId) return site;

            return {
              ...site,
              ouvrages: [...site.ouvrages, newOuvrage],
            };
          }),
        };
      });

      setActiveOuvrageId(newOuvrage.id);
      setActiveLevelId(null);
      }, [activeSiteId, activeSite, project]);

  const deleteOuvrage = useCallback(
    (ouvrageId) => {
      if (!activeSiteId || !ouvrageId) return;

      const currentSite = sites.find((site) => site.id === activeSiteId);
      if (!currentSite) return;

      const nextOuvrages = currentSite.ouvrages.filter(
        (ouvrage) => ouvrage.id !== ouvrageId
      );

      const nextOuvrage = nextOuvrages[0] ?? null;
      const nextLevel = nextOuvrage?.levels?.[0] ?? null;

      setProject((prev) => {
        if (!prev) return prev;

        return {
          ...prev,
          sites: prev.sites.map((site) => {
            if (site.id !== activeSiteId) return site;

            return {
              ...site,
              ouvrages: nextOuvrages,
            };
          }),
        };
      });

      setActiveOuvrageId(nextOuvrage?.id ?? null);
      setActiveLevelId(nextLevel?.id ?? null);
    },
    [activeSiteId, sites]
  );

  /*
  ================================================================
  GESTION DES NIVEAUX
  ================================================================
  */

  const createLevel = useCallback(() => {
    if (!activeSiteId || !activeOuvrageId) return;

    const nextOrder = (activeOuvrage?.levels?.length ?? 0) + 1;
    const newLevel = createLevelObject(nextOrder, null, {
      ProjectCode: project?.id ?? null,
      SiteCode: activeSite?.id ?? null,
      AssetCode: activeOuvrage?.AssetCode ?? activeOuvrage?.id ?? null,
      Elevation: activeOuvrage?.levels?.length === 0 ? 0 : nextOrder > 1 ? (nextOrder - 1) * 3 : 0,
      Height: 3,
    });

    setProject((prev) => {
      if (!prev) return prev;

      return {
        ...prev,
        sites: prev.sites.map((site) => {
          if (site.id !== activeSiteId) return site;

          return {
            ...site,
            ouvrages: site.ouvrages.map((ouvrage) => {
              if (ouvrage.id !== activeOuvrageId) return ouvrage;

              return {
                ...ouvrage,
                levels: [...ouvrage.levels, newLevel],
              };
            }),
          };
        }),
      };
    });

    setActiveLevelId(newLevel.id);
    }, [activeSiteId, activeOuvrageId, activeOuvrage, activeSite, project]);

  const createLevelFromGeometry = useCallback(
    (geometry) => {
      if (!activeSiteId || !activeOuvrageId || !geometry) return;

      const nextOrder = (activeOuvrage?.levels?.length ?? 0) + 1;
      const newLevel = createLevelObject(nextOrder, geometry, {
        ProjectCode: project?.id ?? null,
        SiteCode: activeSite?.id ?? null,
        AssetCode: activeOuvrage?.AssetCode ?? activeOuvrage?.id ?? null,
        Elevation: activeOuvrage?.levels?.length === 0 ? 0 : nextOrder > 1 ? (nextOrder - 1) * 3 : 0,
        Height: 3,
      });

      setProject((prev) => {
        if (!prev) return prev;

        return {
          ...prev,
          sites: prev.sites.map((site) => {
            if (site.id !== activeSiteId) return site;

            return {
              ...site,
              ouvrages: site.ouvrages.map((ouvrage) => {
                if (ouvrage.id !== activeOuvrageId) return ouvrage;

                return {
                  ...ouvrage,
                  levels: [...ouvrage.levels, newLevel],
                };
              }),
            };
          }),
        };
      });

      setActiveLevelId(newLevel.id);
    },
       [activeSiteId, activeOuvrageId, activeOuvrage, activeSite, project]
  );

  const deleteLevel = useCallback(
    (levelId) => {
      if (!activeSiteId || !activeOuvrageId || !levelId) return;

      const currentSite = sites.find((site) => site.id === activeSiteId);
      const currentOuvrage =
        currentSite?.ouvrages?.find((ouvrage) => ouvrage.id === activeOuvrageId) ??
        null;

      if (!currentOuvrage) return;

      const nextLevels = currentOuvrage.levels.filter(
        (level) => level.id !== levelId
      );
      const nextLevel = nextLevels[0] ?? null;

      setProject((prev) => {
        if (!prev) return prev;

        return {
          ...prev,
          sites: prev.sites.map((site) => {
            if (site.id !== activeSiteId) return site;

            return {
              ...site,
              ouvrages: site.ouvrages.map((ouvrage) => {
                if (ouvrage.id !== activeOuvrageId) return ouvrage;

                return {
                  ...ouvrage,
                  levels: nextLevels,
                };
              }),
            };
          }),
        };
      });

      setActiveLevelId(nextLevel?.id ?? null);
    },
    [activeSiteId, activeOuvrageId, sites]
  );

  /*
  ================================================================
  EXPORT
  ================================================================
  */

  return {
    project,

    sites,

    activeSiteId,
    setActiveSiteId,

    activeOuvrageId,
    setActiveOuvrageId,

    activeLevelId,
    setActiveLevelId,

    activeSite,
    activeOuvrage,
    activeLevel,

    getActiveSiteEntities,
    getActiveOuvrageEntities,
    getActiveEntities,
    getEntitiesForActiveContext,

    updateEntitiesForActiveSite,
    updateEntitiesForActiveOuvrage,
    updateEntitiesForActiveLevel,

    createEntityOnSite,
    createEntityOnOuvrage,
    createEntityOnLevel,

    createInterfaceObjectOnSite,
    createInterfaceObjectOnOuvrage,
    createInterfaceObjectOnLevel,

    updateOuvrageById,
    updateLevelById,

    createSite,
    deleteSite,
    updateSiteAttributes, // Ajout de la fonction dans l'export

    createOuvrage,
    createOuvrageFromGeometry,
    deleteOuvrage,

    createLevel,
    createLevelFromGeometry,
    deleteLevel,

    loading,
  };
}